/**
 * English: Org context bar — selected org chip, picker, link to organizations.
 * Thai: แถบบริบทองค์กร — chip org ที่เลือก, ตัวเลือก, ลิงก์ไป organizations
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BuildingOffice2Icon } from '@heroicons/react/24/outline';
import type { EnabledOrg } from './useAgentManagementScope';

interface AgentManagementOrgBarProps {
  orgName: string;
  enabledOrgs: EnabledOrg[];
  orgsLoading: boolean;
  onOrgChange: (orgName: string) => Promise<{ ok: true } | { ok: false; error: string }>;
}

export default function AgentManagementOrgBar({
  orgName,
  enabledOrgs,
  orgsLoading,
  onOrgChange,
}: AgentManagementOrgBarProps) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const selectedOrg = enabledOrgs.find((o) => o.org_name === orgName);

  const handlePick = async (name: string) => {
    if (!name || name === orgName) return;
    setBusy(true);
    setError('');
    const result = await onOrgChange(name);
    setBusy(false);
    if (!result.ok) setError(result.error);
  };

  if (!orgName) {
    return (
      <section className="overflow-hidden rounded-2xl border border-app-border/80 bg-app-surface shadow-sm">
        <div className="border-b border-app-border/60 bg-app-muted/15 px-5 py-4 sm:px-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-ink">{t('agents.scopeStationsTitle')}</h2>
              <p className="mt-0.5 text-sm text-app-text-dim">{t('agents.scopeNoOrgHint')}</p>
            </div>
            <Link
              to="/organizations"
              className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-app-border bg-app-bg px-3 py-2 text-sm font-medium text-app-accent transition-colors hover:bg-app-muted/40"
            >
              <BuildingOffice2Icon className="h-4 w-4" />
              {t('dashboard.manageOrgs', { defaultValue: 'จัดการองค์กร' })}
            </Link>
          </div>
        </div>
        <div className="p-8 sm:p-10 text-center">
          <BuildingOffice2Icon className="mx-auto h-10 w-10 text-app-text-dim/60" />
          <p className="mt-4 text-lg font-semibold text-ink">{t('agents.scopeNoOrg')}</p>
          <p className="mt-2 mx-auto max-w-md text-sm text-app-text-sec">{t('agents.scopeNoOrgHint')}</p>
          {error ? (
            <p className="mt-3 text-sm text-rose-600 dark:text-rose-400">{error}</p>
          ) : null}
          {orgsLoading ? (
            <p className="mt-6 text-sm text-app-text-dim">{t('common.loading')}</p>
          ) : enabledOrgs.length === 0 ? (
            <p className="mt-6 text-sm text-app-text-sec">
              <Link to="/organizations" className="font-medium text-app-accent underline">
                {t('sidebar.menu.organizations')}
              </Link>
            </p>
          ) : (
            <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <select
                className="w-full max-w-xs rounded-lg border border-app-border bg-app-bg px-3 py-2.5 text-sm text-app-text focus:outline-none focus:ring-2 focus:ring-app-accent/30 disabled:opacity-50"
                value=""
                disabled={busy}
                onChange={(e) => void handlePick(e.target.value)}
                aria-label={t('agents.scopeChooseOrg')}
              >
                <option value="">{t('agents.scopeChooseOrg')}</option>
                {enabledOrgs.map((o) => (
                  <option key={o.org_name} value={o.org_name}>
                    {o.display_name || o.org_name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="overflow-hidden rounded-2xl border border-app-border/80 bg-app-surface shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-app-border/60 bg-app-muted/15 px-5 py-4 sm:px-6">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-app-text-dim">
            {t('agents.scopeSelectedOrg')}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-2 rounded-lg border border-app-accent/30 bg-app-accent/10 px-3 py-1.5 text-sm font-medium text-app-text">
              <BuildingOffice2Icon className="h-4 w-4 shrink-0 text-app-accent" />
              <span className="font-mono">{orgName}</span>
              {selectedOrg?.display_name && selectedOrg.display_name !== orgName ? (
                <span className="text-xs text-app-text-dim truncate max-w-[12rem]">
                  {selectedOrg.display_name}
                </span>
              ) : null}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            className="rounded-lg border border-app-border bg-app-bg px-3 py-2 text-sm text-app-text focus:outline-none focus:ring-2 focus:ring-app-accent/30 disabled:opacity-50 sm:min-w-[10rem]"
            value={orgName}
            disabled={busy || orgsLoading}
            onChange={(e) => void handlePick(e.target.value)}
            aria-label={t('agents.scopeChooseOrg')}
          >
            {enabledOrgs.map((o) => (
              <option key={o.org_name} value={o.org_name}>
                {o.display_name || o.org_name}
              </option>
            ))}
          </select>
          <Link
            to="/organizations"
            className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-app-border bg-app-bg px-3 py-2 text-sm font-medium text-app-accent transition-colors hover:bg-app-muted/40"
          >
            <BuildingOffice2Icon className="h-4 w-4" />
            {t('dashboard.manageOrgs', { defaultValue: 'จัดการองค์กร' })}
          </Link>
        </div>
      </div>
      {error ? (
        <p className="px-5 py-2 text-sm text-rose-600 dark:text-rose-400 sm:px-6">{error}</p>
      ) : null}
    </section>
  );
}
